import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api";
import {
  Button,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Label,
  Skeleton,
  useToast,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@wondermaps/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt } from "@fortawesome/pro-solid-svg-icons/faBolt";
import { faEnvelope } from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import { faSliders } from "@fortawesome/pro-solid-svg-icons/faSliders";
import { SuperadminHeader } from "../components/superadmin/SuperadminHeader";

export const Route = createFileRoute("/superadmin/reports")({
  component: SuperadminReportsPage,
});

type Organization = {
  id: string;
  name: string;
  slug: string;
  type: string | null;
  memberCount: number;
  createdAt: string;
  settings?: {
    metricsEmail?: {
      enabled: boolean;
      frequency: "weekly" | "monthly";
      dayOfWeek?: number;
      dayOfMonth?: number;
      excludedMemberIds?: string[];
    };
  };
};

const DAYS_OF_WEEK = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

function SuperadminReportsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [configOrgId, setConfigOrgId] = useState<string | null>(null);

  // Fetch orgs
  const { data: orgsData, isLoading: isOrgsLoading } = useQuery({
    queryKey: ["superadmin", "orgs"],
    queryFn: async () => {
      const res = await api.orgs.admin.$get();
      if (!res.ok) throw new Error("Failed to fetch organizations");
      return res.json() as Promise<{ orgs: Organization[] }>;
    },
  });

  // Send now mutation
  const sendNow = useMutation({
    mutationFn: async (orgId: string) => {
      const res = await api.reports.send[":orgId"].$post({
        param: { orgId },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error((err as { error?: string }).error ?? "Send failed");
      }
      return res.json();
    },
    onSuccess: (_, orgId) => {
      const org = orgs.find((o) => o.id === orgId);
      toast({ title: `Report sent to ${org?.name ?? "org"}` });
    },
    onError: (err) => {
      toast({
        title: "Send failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  // Preview mutation
  const sendPreview = useMutation({
    mutationFn: async (orgId: string) => {
      const res = await api.reports.preview[":orgId"].$post({
        param: { orgId },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error((err as { error?: string }).error ?? "Preview failed");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Test email sent to your inbox" });
    },
    onError: (err) => {
      toast({
        title: "Preview failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  // Save settings mutation
  const saveSettings = useMutation({
    mutationFn: async ({
      orgId,
      metricsEmail,
    }: {
      orgId: string;
      metricsEmail: NonNullable<Organization["settings"]>["metricsEmail"];
    }) => {
      const res = await api.orgs[":orgId"].settings.$patch({
        param: { orgId },
        json: { metricsEmail } as Record<string, unknown>,
      });
      if (!res.ok) throw new Error("Failed to save settings");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["superadmin", "orgs"] });
      toast({ title: "Settings saved" });
    },
    onError: (err) => {
      toast({
        title: "Save failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const orgs = orgsData?.orgs ?? [];
  const configOrg = orgs.find((o) => o.id === configOrgId);

  return (
    <div className="flex h-screen flex-col bg-background">
      <SuperadminHeader
        title="Metrics Email Reports"
        subtitle="Configure and send analytics digest emails per org"
      />

      {/* Content */}
      <main className="flex-1 overflow-auto p-6">
        <div className="container mx-auto max-w-4xl space-y-6">
          {isOrgsLoading ? (
            <Card>
              <CardContent className="p-6">
                <Skeleton className="h-40 w-full" />
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Organizations</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Organization</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Frequency</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orgs.map((org) => {
                      const config = org.settings?.metricsEmail;
                      const isEnabled = config?.enabled ?? false;
                      return (
                        <TableRow key={org.id}>
                          <TableCell className="font-medium">
                            {org.name}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={isEnabled ? "default" : "secondary"}
                            >
                              {isEnabled ? "Enabled" : "Off"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {isEnabled
                              ? config?.frequency === "weekly"
                                ? `Weekly (${DAYS_OF_WEEK[config?.dayOfWeek ?? 1]})`
                                : `Monthly (${config?.dayOfMonth ?? 1}${ordSuffix(config?.dayOfMonth ?? 1)})`
                              : "—"}
                          </TableCell>
                          <TableCell className="text-right space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setConfigOrgId(org.id)}
                            >
                              <FontAwesomeIcon icon={faSliders} className="h-3.5 w-3.5 mr-1" />
                              Configure
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => sendPreview.mutate(org.id)}
                              disabled={sendPreview.isPending}
                            >
                              <FontAwesomeIcon icon={faEnvelope} className="h-3.5 w-3.5 mr-1" />
                              Test
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => sendNow.mutate(org.id)}
                              disabled={sendNow.isPending}
                            >
                              <FontAwesomeIcon icon={faBolt} className="h-3.5 w-3.5 mr-1" />
                              Send
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Config Dialog */}
      {configOrg && (
        <ConfigDialog
          org={configOrg}
          onClose={() => setConfigOrgId(null)}
          onSave={(metricsEmail) =>
            saveSettings.mutate(
              { orgId: configOrg.id, metricsEmail },
              { onSuccess: () => setConfigOrgId(null) }
            )
          }
          isSaving={saveSettings.isPending}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Config Dialog
// ---------------------------------------------------------------------------

function ConfigDialog({
  org,
  onClose,
  onSave,
  isSaving,
}: {
  org: Organization;
  onClose: () => void;
  onSave: (
    config: NonNullable<Organization["settings"]>["metricsEmail"]
  ) => void;
  isSaving: boolean;
}) {
  const existing = org.settings?.metricsEmail;
  const [enabled, setEnabled] = useState(existing?.enabled ?? false);
  const [frequency, setFrequency] = useState<"weekly" | "monthly">(
    existing?.frequency ?? "monthly"
  );
  const [dayOfWeek, setDayOfWeek] = useState(existing?.dayOfWeek ?? 1);
  const [dayOfMonth, setDayOfMonth] = useState(existing?.dayOfMonth ?? 1);

  const handleSave = () => {
    onSave({
      enabled,
      frequency,
      ...(frequency === "weekly" ? { dayOfWeek } : { dayOfMonth }),
      excludedMemberIds: existing?.excludedMemberIds ?? [],
    });
  };

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Metrics Email — {org.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4">
          {/* Enable toggle */}
          <div className="flex items-center justify-between">
            <Label htmlFor="enabled">Enable metrics email</Label>
            <Switch
              id="enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
            />
          </div>

          {enabled && (
            <>
              {/* Frequency */}
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select
                  value={frequency}
                  onValueChange={(v) =>
                    setFrequency(v as "weekly" | "monthly")
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Day picker */}
              {frequency === "weekly" ? (
                <div className="space-y-2">
                  <Label>Day of week</Label>
                  <Select
                    value={String(dayOfWeek)}
                    onValueChange={(v) => setDayOfWeek(Number(v))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map((day, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Day of month</Label>
                  <Select
                    value={String(dayOfMonth)}
                    onValueChange={(v) => setDayOfMonth(Number(v))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map(
                        (d) => (
                          <SelectItem key={d} value={String(d)}>
                            {d}
                            {ordSuffix(d)}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

function ordSuffix(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}
